import EventEmitter from "EventEmitter";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";

import { TweenLite } from "gsap";

import disposeObject3D from "../../utils/disposeObject3D";
import MaterialManager from "./material-manager";
import pkg from "../../../package.json";

export default class Wheelchair extends EventEmitter {
  constructor(scene, renderer, country) {
    super();

    this.debug = false;
    this.dracoDecoderPath = (pkg.homepage || "") + "/gltf/";
    this.basepath = (pkg.homepage || "") + "/wheelchair";
    this.isGermany = country === "GERMANY";
    this.f55Angle = 3;
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath(this.dracoDecoderPath);
    this.dracoLoader.preload();

    this.mainGroup = new THREE.Group();

    this.scene = scene;
    this.renderer = renderer;
    this.materials = [];
    this.materialManager = new MaterialManager(this.scene, this.renderer);

    this.scene.add(this.mainGroup);
  }

  get UseShadows() {
    return this._useShadows;
  }

  set UseShadows(useShadows) {
    this._useShadows = useShadows;
  }

  loadParts(parts, materialGroups = false, initialLoad = false) {
    return new Promise((resolve, reject) => {
      const promises = [];
      for (let part in parts) {
        const partPath = parts[part];

        if (partPath === "false") {
          // src/path can be false when an option is "NONE"
          // We check for a string  because we send all path values as strings
          // from Viewer and boolean false will be casted to string "false"
          continue;
        }

        promises.push(this._loadPart(part, partPath, materialGroups));
      }

      Promise.all(promises)
        .then((result) => {
          result.forEach(({ oldPart, newPart }) => {
            const isF55 = newPart.name === "f55";
            const target = isF55 ? this.scene : this.mainGroup;

            if (isF55) {
              this.mainGroup.rotation.x = THREE.Math.degToRad(this.f55Angle);
              newPart.position.z += 0.02;
            }

            target.add(newPart);
            if (oldPart) {
              target.remove(oldPart);
              disposeObject3D(oldPart);
            }
          });

          if (initialLoad) {
            this.emit("partsloaded");
          }

          resolve();
        })
        .catch((error) => {
          reject(error);
          console.error("loadParts error:", error);
        });
    });
  }

  /**
   * This shouldn't be called directly. Even if only loading one part,
   * use loadParts() as it may handle additional pre/post loading
   * logic
   *
   * @param {string} path - part path
   */
  _loadPart(name, path, materialGroups) {
    return new Promise((resolve, reject) => {
      if (!path) {
        reject("Invalid part path");
      }

      const loader = new GLTFLoader();
      loader.setDRACOLoader(this.dracoLoader);

      loader.load(
        `${this.basepath}/${path}`,
        (gltf) => {
          // Add name to scene for future removal/cleanup
          gltf.scene.name = name;

          if (!materialGroups) {
            // Resolve with a ref to the new part to be added to scene
            // and a ref to the old part to be removed and disposed
            return resolve({
              newPart: gltf.scene,
              oldPart: this.scene.getObjectByName(name),
            });
          }

          // APPLY MATERIALS

          const materialPromises = [];

          materialGroups.forEach((materialGroup) => {
            if (materialGroup.targets[name]) {
              const materialPromise = this.materialManager
                .getMaterialByName(materialGroup.material)
                .then((targetMaterial) => {
                  const targetChildren = materialGroup.targets[name];
                  const targetChildrenLength = targetChildren.length;
                  let i;

                  this.debug && console.groupCollapsed(name);

                  gltf.scene.traverse((child) => {
                    if (!(child instanceof THREE.Mesh)) {
                      return;
                    }

                    // keep the original material name in a child userData property.
                    // We do this for subsequent material changes because we loose
                    // the original name after setting a material.
                    // Overriding the material.name doesn't work because materials
                    // are shared and thus, linked by reference
                    let childMatName = child.userData.originalMatName;

                    if (!childMatName) {
                      childMatName = child.material.name;
                      child.userData.originalMatName = childMatName;
                    }

                    // Disable Sopur Logo on not German
                    // Quick fix to be changed in a feature
                    if (name === "backrest") {
                      if (
                        [
                          "pasted__yellow",
                          "pasted__white",
                          "pasted__black",
                          "pasted__grey",
                        ].includes(childMatName)
                      ) {
                        // Quicky
                        child.visible = this.isGermany ? false : true;
                      } else if (
                        [
                          "pasted__backupholstery_badgecolour1_option1",
                          "pasted__backupholstery_badgecolour2_option1",
                          "pasted__backupholstery_badge_option13",
                        ].includes(childMatName)
                      ) {
                        // Sopur
                        child.visible = this.isGermany ? true : false;
                      }
                    }

                    if (name === "sideguards") {
                      if (["Sideguard_Logo_Quickie"].includes(childMatName)) {
                        child.visible = this.isGermany ? false : true;
                      } else if (
                        ["Sideguard_Logo_Sopur"].includes(childMatName)
                      ) {
                        child.visible = this.isGermany ? true : false;
                      }
                    }

                    // SHADOWS
                    // Still in test as we think it affect performance
                    // Make sure we move that so some state where when we drop from 45fps we turn it off
                    // Make a function taht update the existant mesh
                    if (this.UseShadows) {
                      if (name === "cushion" || name === "backrest") {
                        child.receiveShadow = true;
                      }

                      if (child.material.name.includes("bolts")) {
                        child.receiveShadow = false;
                        child.castShadow = false;
                      } else {
                        child.castShadow = true;
                      }
                    }

                    this.debug && console.log(childMatName);

                    for (i = 0; i < targetChildrenLength; i++) {
                      const matches =
                        name === "rear_wheels"
                          ? childMatName === targetChildren[i]
                          : childMatName.includes(targetChildren[i]);

                      if (matches) {
                        if (targetMaterial.name === "HIDE") {
                          child.visible = false;
                        }

                        child.material = targetMaterial;
                        break;
                      }
                    }
                  });

                  this.debug && console.groupEnd();
                });

              materialPromises.push(materialPromise);
            }
          });

          Promise.all(materialPromises).then((result) => {
            // Resolve with a ref to the new part to be added to scene
            // and a ref to the old part to be removed and disposed
            resolve({
              newPart: gltf.scene,
              oldPart: this.scene.getObjectByName(name),
            });
          });
        },
        null, // progress
        (error) => reject({ error: error, path: `${this.basepath}/${path}` })
      );
    });
  }

  updatePartsMaterials(parts, materialName) {
    return new Promise((resolve, reject) => {
      this.materialManager.getMaterialByName(materialName).then((material) => {
        for (const key in parts) {
          if (parts.hasOwnProperty(key)) {
            const part = this.scene.getObjectByName(key);
            const children = parts[key];
            const length = children.length;
            let i;

            if (!part || part === undefined) {
              console.warn(`Unable to get "${key}" part`);
              continue;
            }

            this.debug && console.groupCollapsed(key);
            part.traverse((child) => {
              if (!(child instanceof THREE.Mesh)) {
                return;
              }

              const childMatName = child.userData.originalMatName;
              this.debug && console.log(childMatName);
              for (i = 0; i < length; i++) {
                if (childMatName.includes(children[i])) {
                  child.material = material;
                  break;
                }
              }
            });
            this.debug && console.groupEnd();
          }
        }

        resolve();
      });
    });
  }

  /**
   * Removes a part from the scene and disposes object
   *
   * Usually _loadPart sets the part that should be removed (replaced), but in
   * one case (freestyle back) we need to remove a part (push bar) without
   * replacing.
   *
   * @param {string} name
   */
  unloadPart(name) {
    const part = this.scene.getObjectByName(name);
    const isF55 = name === "f55";
    const target = isF55 ? this.scene : this.mainGroup;

    if (!part) {
      console.warn("Attempt to remove a part that is not in the scene:", name);
      return;
    }

    if (isF55) {
      this.mainGroup.rotation.x = THREE.Math.degToRad(0);
    }

    this.debug && console.log("Removed part from scene", name);
    target.remove(part);
    disposeObject3D(part);
  }

  /**
   * Update materials HDR from the environment
   * Called by Viewer when Environment is loaded
   *
   * @todo check if there's no problem with setting it ahead of time when
   * parts/materials aren't loaded yet
   *
   * @param {hdr} hdr - HDR
   */
  setHdr(hdr) {
    this.HDR = hdr;
    this.materialManager.setHdr(this.HDR);

    if (!this.materials.length) {
      // console.log("setHdr() has no materials");
      return;
    }

    this.materials.forEach((m) => {
      m.envMap = this.HDR;
      m.needsUpdate = true;
    });
  }

  /**
   * Update HDR from the environment
   * @param {string} partName - name of the part ( currently related with the name of the material )
   * @param {#} color - color
   */
  changeMaterialColor(partName, color) {
    const material = this.materialManager.getMaterialFromId(partName);

    if (!material) {
      console.log("Material not found:", partName);
      return;
    }

    this._lerpMaterialColor(material, color, 0);
  }

  /**
   * Lerp material Color
   *
   * @param {material} material - Material
   * @param {#} color - color
   * @param {float} time - time
   */
  _lerpMaterialColor(material, color, time = 0.8) {
    const colorTo = new THREE.Color(material.color.getHex());
    const value = new THREE.Color(color);

    TweenLite.to(colorTo, time, {
      r: value.r,
      g: value.g,
      b: value.b,
      onUpdate: () => {
        material.color = colorTo;
        material.needsUpdate = true;
      },
    });
  }

  /**
   * Start Animation
   * @todo Implement or remove
   *
   * @param {string} clipName - clipName
   */
  animateChair(clipName) {
    const clip = THREE.AnimationClip.findByName(this.clips, clipName);
    const action = this.mixer.clipAction(clip);
    action.play();
  }

  /**
   * Called by Viewer updater (animate())
   */
  update() {
    if (this.mixer) {
      this.mixer.update(0.01);
    }
  }
}
